import isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { getActionAriaLabel } from '@/utils/urlUtils';

import { Icon } from '@/components/Icon';
import type {
  ContentfulFooterFragment,
  ContentfulMainMenuItemFragment,
} from '@/graphql/contentful/api';
import { useContactInfo } from '@/hooks/useContactInfo';
import { getBrandConfig } from '@/lib/configuration/configuration';
import { CatalogDownloadDesc } from '@/messages/catalogDownload';
import { commonMessages } from '@/messages/common';
import { NewsLetterDesc } from '@/messages/newsletter';
import { useRegionProvider } from '@/providers/RegionProvider';

import { Flex } from '../Flex';
import { Logo } from '../Logo';
import { NewsLetterModal } from '../NewsLetterModal';
import { Typography } from '../Typography';
import { ContactInfo, LinkItem, Links, Menu } from './components';

import * as S from './Footer.styles';

const CatalogDownloadModal = dynamic(() =>
  import('@/components/CatalogDownloadModal').then(
    (mod) => mod.CatalogDownloadModal,
  ),
);

export type FooterColorVariant =
  | 'default'
  | 'defaultInverted'
  | 'primary'
  | 'primaryInverted';

interface FooterProps {
  disableListenEvent?: boolean;
  footerData: ContentfulFooterFragment;
}

export const Footer = ({ disableListenEvent, footerData }: FooterProps) => {
  const [isShowNewsLetterPopup, setIsShowNewsLetterPopup] = useState(false);
  const [isShowDownloadCatalogPopup, setIsShowDownloadCatalogPopup] =
    useState(false);
  const contactInfo = useContactInfo();
  const { locale } = useRegionProvider();

  const { formatMessage } = useIntl();
  const defaultCopyRight = formatMessage(commonMessages.defaultCopyRight);
  const year = new Date().getFullYear();
  const copyRightWithYear = defaultCopyRight.replaceAll(
    '{year}',
    year.toString(),
  );

  const toggleNewsLetterPopup = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setIsShowNewsLetterPopup(!isShowNewsLetterPopup);
  };

  const toggleDownloadCatalogPopup = () => {
    setIsShowDownloadCatalogPopup(!isShowDownloadCatalogPopup);
  };

  const brandConfig = getBrandConfig();
  const isSimpleFooter = brandConfig.simpleFooter;

  const handleNewsletter = () => {
    setIsShowNewsLetterPopup(true);
  };
  const handleCatalogDownload = () => {
    setIsShowDownloadCatalogPopup(true);
  };

  useEffect(() => {
    if (disableListenEvent) return;

    window.addEventListener('newsletter', handleNewsletter);
    window.addEventListener('catalogDownload', handleCatalogDownload);
    return () => {
      window.removeEventListener('newsletter', handleNewsletter);
      window.removeEventListener('catalogDownload', handleCatalogDownload);
    };
  }, [handleNewsletter, disableListenEvent]);
  return (
    <>
      <S.Wrapper data-test-id='FooterWrapper' forwardedAs='footer'>
        <S.FooterBox>
          {isEmpty(footerData) ? (
            <S.DefaultCopyRightWrapper style={{ color: 'white' }}>
              {copyRightWithYear}
            </S.DefaultCopyRightWrapper>
          ) : (
            <S.InnerWrapper pb={{ xs: 8, md: 4 }} pt={4}>
              {(footerData.displayNewsletterSubscribe ||
                footerData.displayContactInformation) && (
                <S.ContactInfoWrapper>
                  {footerData.displayNewsletterSubscribe && (
                    <S.Subscribe
                      aria-label={
                        getActionAriaLabel('submit_newsletter', locale.code) ||
                        'Subscribe to newsletter'
                      }
                      data-test-id='FooterSubscribe'
                      onClick={toggleNewsLetterPopup}
                    >
                      <Flex>
                        <Typography
                          className='subscribeText'
                          data-test-id='FooterSubscribeText'
                        >
                          {footerData.subscribeToNewsletterText}
                        </Typography>
                        <Icon
                          data-test-id='FooterSubscribeIcon'
                          name='chevronRight'
                          size='m'
                          variant='regular'
                        />
                      </Flex>
                    </S.Subscribe>
                  )}
                  {footerData.displayContactInformation && (
                    <Footer.ContactInfo contactInfo={contactInfo} />
                  )}
                </S.ContactInfoWrapper>
              )}
              {!isSimpleFooter && (
                <Footer.Menu
                  footerData={footerData}
                  openDownloadCatalogPopup={toggleDownloadCatalogPopup}
                />
              )}
              {isSimpleFooter && (
                <S.LogoWrapper>
                  <Logo variant='logoLine' />
                </S.LogoWrapper>
              )}
              {footerData.logo?.url && (
                <S.LogoWrapper>
                  <Logo
                    disableClick={isEmpty(footerData.logoLink)}
                    heightDesktop={footerData.logoHeightDesktop}
                    heightMobile={footerData.logoHeightMobile}
                    link={footerData.logoLink}
                    src={footerData.logo.url}
                    widthDesktop={footerData.logoWidthDesktop}
                    widthMobile={footerData.logoWidthMobile}
                  />
                </S.LogoWrapper>
              )}
              <S.Break />
              <Footer.Links
                copyRight={footerData.copyrightText ?? ''}
                data-test-id='FooterLinks'
                links={
                  footerData.secondaryMenuItemsCollection
                    ?.items as ContentfulMainMenuItemFragment[]
                }
              />
            </S.InnerWrapper>
          )}
        </S.FooterBox>
        <S.Tail />
      </S.Wrapper>
      <NewsLetterModal
        isOpen={isShowNewsLetterPopup}
        onClose={() => setIsShowNewsLetterPopup(false)}
      >
        <S.NewsLetterDesc
          color='default'
          fontFamily='openSans'
          mb={2}
          mt={2}
          textAlign='center'
        >
          {NewsLetterDesc}
        </S.NewsLetterDesc>
      </NewsLetterModal>
      {isShowDownloadCatalogPopup && (
        <CatalogDownloadModal
          isOpen={isShowDownloadCatalogPopup}
          onClose={() => setIsShowDownloadCatalogPopup(false)}
        >
          <Typography
            color='default'
            fontFamily='openSans'
            mb={2}
            mt={2}
            textAlign='center'
          >
            {CatalogDownloadDesc}
          </Typography>
        </CatalogDownloadModal>
      )}
    </>
  );
};

Footer.Links = Links;
Footer.Link = LinkItem;
Footer.ContactInfo = ContactInfo;
Footer.Menu = Menu;

Footer.displayName = 'Footer';
